/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import Component from '@glimmer/component';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS } from 'cio/models/account';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import MomentService from 'cio/services/moment';
import { tracked } from '@glimmer/tracking';
import { FlyInputEvent } from '@customerio/fly-components/types/fly-components/events';
import FormValidations from 'cio/utils/form-validations';
import { controlSupportTeamAccessFieldValidation as Validations } from 'cio/components/support-form/control-team-access-field';
import { timestampToInlineLongDate } from 'cio/utils/time';

interface SupportFormArgs {
  replyTo: string;
  userEmail: string;
  onClose: () => unknown;
  onSubmit: (feedback: { comment: string; goal: string; consentToAccess?: boolean }) => unknown;
}

export default class SupportForm extends Component<SupportFormArgs> {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service moment!: MomentService;

  @tracked comment = '';
  @tracked consentToAccess!: boolean;
  @tracked goal = '';
  @tracked validations: FormValidations;
  @tracked formValues = {
    daysLimit: DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS,
  };

  get isDisabled() {
    return isEmpty(this.comment) || isEmpty(this.goal);
  }

  get displayAccessConsentExplanation() {
    return (
      !this.auth.hasPermission('account/support_access/edit') &&
      !(this.account.current?.supportTeamAccessEnabled ?? false)
    );
  }

  @computed('account.current.{supportTeamAccessEnabled,accessGrantedUntil}')
  get supportTeamAccessDaysFieldLabelPrefix() {
    if (!this.account.current?.supportTeamAccessEnabled) {
      return '';
    }

    let date = timestampToInlineLongDate(this.account.current.accessGrantedUntil, this.moment);
    return `Access is currently granted until ${date}.`;
  }

  get noAccessOptionLabel() {
    return this.account.current?.supportTeamAccessEnabled ? 'No (revoke access)' : 'No';
  }

  constructor(owner: unknown, args: SupportFormArgs) {
    super(owner, args);
    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'formValues',
    });

    this.consentToAccess = this.account.current?.supportTeamAccessEnabled ?? false;
  }

  @action
  async validateValues() {
    try {
      await this.validations.validateModel();
    } catch {
      return;
    }
  }

  @action
  async submitAction(evt: FlyInputEvent<HTMLInputElement>) {
    evt.preventDefault();
    let { comment, goal, consentToAccess } = this;
    if (this.auth.hasPermission('account/support_access/edit')) {
      let account = this.account.current;

      await this.validateValues();

      try {
        await account.setSupportAccessConsent(consentToAccess ? this.formValues.daysLimit : 0);
      } catch {
        account?.rollbackAttributes();
        this.alerts.error(
          'privacy.update-support-access',
          'Could not update your account support team access settings.',
        );
      }
      this.args.onSubmit({ comment, goal, consentToAccess });
    } else {
      // Read-only users have no permission to change privacy settings.
      this.args.onSubmit({ comment, goal });
    }
  }

  @action
  onSupportTeamAccessTimeLimitChanged(value: number) {
    this.validations.clearAttributeErrors('daysLimit');
    this.formValues = { daysLimit: value };
  }
}
